import { useCallback, useEffect, useState } from "react";

import * as api from "../../lib/api.js";
import * as K from "../../lib/const.js";
import type { WalletOut } from "../contract/api";
import { t, type StringKey } from "../contract/strings";
import { forgetHomeData, launchWalletRead } from "../engine/home-data";

/**
 * The host's credit balance: `GET /v1/billing/wallet`, read by the Account screen's balance
 * section and by home's record plate (#686).
 *
 * The server keeps the balance in credits, one per meeting-minute. Everything a person
 * reads is that raw credit count ("135 credits left"), never a dollar amount: the web sells
 * about 60 credits a dollar and the apps fewer, so a dollar figure would say something different
 * on every platform, while a credit is a credit everywhere (#651). Only the pack prices are
 * money, in the host's own currency where the packs have a local price (the server says
 * which, from the country the edge resolved), and in dollars everywhere else.
 */
export type Wallet = WalletOut;

/** Why `POST /v1/sessions` refuses a new meeting, as the server's stable code says. */
export type StartRefusal = NonNullable<WalletOut["start_refusal"]>;

const REFUSALS: ReadonlySet<string> = new Set<StartRefusal>([
  "billing_balance_exhausted",
  "billing_account_blocked",
  "billing_meeting_in_progress",
]);

/**
 * The billing code a refused create carries in its body (`BillingRefusal`), or null for
 * any other failure. The code, not the status: a 402 or 409 can mean other things.
 */
export function billingRefusal(error: unknown): StartRefusal | null {
  const code = (error as { data?: { code?: unknown } } | null)?.data?.code;
  return typeof code === "string" && REFUSALS.has(code) ? (code as StartRefusal) : null;
}

/** The balance as one phrase: credits left, credits short (below zero, #1010), or none. */
export function balanceLine(balance: number): string {
  if (balance > 0) return t("billing.left", { credits: credits(Math.abs(balance)) });
  if (balance < 0) return t("billing.short", { credits: credits(Math.abs(balance)) });
  return t("billing.none_left");
}

/**
 * About how much meeting a pack buys, at the end of its card: "~5 h" for 300 credits.
 *
 * A credit is a minute, so whole hours rounded down -- never more time than the pack
 * gives ("~18 h" for 1,100, not 18.3). Null for a pack under an hour, which shows
 * nothing rather than "~0 h".
 */
export function packHours(credits: number): string | null {
  const hours = Math.floor(credits / 60);
  return hours >= 1 ? t("billing.pack_hours", { hours }) : null;
}

/**
 * A pack's price as the host will be charged it: "$5", "£10", "€5.50", "₹1,500", "¥800".
 *
 * `amountMinor` is in the currency's smallest unit, as the server sends it; how many digits
 * that unit has comes from `Intl`, so JPY, KRW and VND (none) need no table here. A whole
 * amount drops its ".00". `locale` is the browser's unless a test pins one.
 */
export function packPrice(amountMinor: number, currency: string, locale?: string): string {
  const options = { style: "currency", currency } as const;
  const digits = new Intl.NumberFormat(locale, options).resolvedOptions().maximumFractionDigits ?? 2;
  const amount = amountMinor / 10 ** digits;
  const whole = Number.isInteger(amount);
  return new Intl.NumberFormat(locale, {
    ...options,
    minimumFractionDigits: whole ? 0 : digits,
    maximumFractionDigits: whole ? 0 : digits,
  }).format(amount);
}

/**
 * The pack to have selected when the overlay opens: the middle one. While credits are owed
 * and the middle pack would not leave any to meet with, the smallest that would, else the
 * largest. "Leave any" is above zero: the gate refuses a start at exactly zero.
 */
export function defaultPack(wallet: Wallet): string | null {
  const { packs, balance_credits: balance } = wallet;
  if (packs.length === 0) return null;
  const middle = Math.floor(packs.length / 2);
  const fits = packs.slice(middle).find((p) => balance + p.credits > 0);
  return (fits ?? packs[packs.length - 1]).pack;
}

/**
 * Whether a pack is the one labelled "Best value": the largest, when there is a choice. It
 * is the cheapest per credit in every market (1,200 credits for $19 rather than $20).
 */
export function isBestValue(wallet: Wallet, pack: string): boolean {
  return wallet.packs.length > 1 && wallet.packs[wallet.packs.length - 1].pack === pack;
}

// --- monthly plans (#875) ---------------------------------------------------------------

/** One plan in the wallet's catalogue, and the subscription the wallet shows. */
export type Plan = NonNullable<Wallet["plans"]>[number];
export type PlanKey = Plan["plan"];
export type ShownSubscription = NonNullable<Wallet["subscription"]>;

const PLAN_ORDER: readonly PlanKey[] = ["basic", "plus", "pro"];
const PLAN_NAME_KEY: Record<PlanKey, StringKey> = {
  basic: "plans.basic",
  plus: "plans.plus",
  pro: "plans.pro",
};

/** Whether this deployment sells plans: the server sends the catalogue only when it does. */
export function plansOn(wallet: Wallet): boolean {
  return (wallet.plans ?? []).length > 0;
}

/** A plan's name, "Basic", "Plus" or "Pro". */
export function planName(plan: PlanKey): string {
  return t(PLAN_NAME_KEY[plan]);
}

/** The plan above this one, the one the upgrade nudge names: none -> Basic -> Plus -> Pro. */
export function nextPlan(plan: PlanKey | null | undefined): PlanKey | null {
  if (!plan) return "basic";
  return PLAN_ORDER[PLAN_ORDER.indexOf(plan) + 1] ?? null;
}

/**
 * The plan the plans open with chosen (#1022): Plus without a plan, and the next plan up on
 * one (Basic chooses Plus, Plus chooses Pro). Nothing on Pro, where checkout is not
 * available, or where that plan can't be bought or moved to here: a plan billed in a store,
 * a failed payment, a plan that is ending. A choice, never a purchase: that is the button.
 */
export function defaultPlan(wallet: Wallet): PlanKey | null {
  if (!wallet.checkout_available) return null;
  const sub = wallet.subscription;
  const plan = sub ? nextPlan(sub.plan) : "plus";
  if (!plan || !(wallet.plans ?? []).some((p) => p.plan === plan)) return null;
  const action = planAction(wallet, plan);
  return action === "choose" || action === "upgrade" ? plan : null;
}

/**
 * Whether a plan card carries Best value (#1022): Pro, the most credits a dollar, as the
 * largest pack does. Never beside Your plan: on the host's own Pro, that pill says it.
 */
export function planBestValue(wallet: Wallet, plan: PlanKey): boolean {
  return plan === "pro" && planAction(wallet, plan) !== "current";
}

/**
 * A plan card's first line (#1022): "900 credits a month · About 15 hours", the hours a
 * meeting's minutes, rounded down as a pack's are.
 */
export function planCreditsLine(credits: number): string {
  return t("plans.credits_month_hours", { credits: credits.toLocaleString(), hours: Math.floor(credits / 60) });
}

/** The plan's Ask AI line: "10 Ask AI questions per meeting", or Pro's unlimited with its asterisk. */
export function planAskAi(plan: Plan): string {
  if (plan.ask_ai_per_meeting == null) return t("plans.ask_ai_unlimited");
  return t("plans.ask_ai_per_meeting", { count: plan.ask_ai_per_meeting });
}

/** A plan's price a month, "$5 a month", or null where the server sent no price. */
export function planPrice(plan: Plan): string | null {
  if (plan.price_minor == null || !plan.price_currency) return null;
  return packPrice(plan.price_minor, plan.price_currency);
}

/**
 * What a plan card offers:
 * - `choose`: no plan yet, so a checkout;
 * - `current`: the host's plan, marked "Your plan";
 * - `upgrade` / `switch`: a higher or a lower plan than an active web plan;
 * - `null`: nothing here. A plan bought in a store is changed in that store; a plan in
 *   grace, on hold or cancelled changes through Manage subscription; a downgrade already
 *   waiting for the renewal is said on the plan card instead.
 */
export type PlanAction = "choose" | "current" | "upgrade" | "switch" | null;

export function planAction(wallet: Wallet, plan: PlanKey): PlanAction {
  const sub = wallet.subscription;
  if (!sub) return "choose";
  if (plan === sub.plan) return "current";
  if (sub.provider !== "dodo" || sub.status !== "active" || sub.pending_plan === plan) return null;
  return PLAN_ORDER.indexOf(plan) > PLAN_ORDER.indexOf(sub.plan) ? "upgrade" : "switch";
}

/** A date as the plan card says it: the day and month, no year, in the viewer's locale (#1010). */
export function planDate(iso: string, locale?: string): string {
  return new Date(iso).toLocaleDateString(locale, { day: "numeric", month: "long" });
}

/** Whether a payment for the plan failed: in grace (it still holds) or on hold (paused). */
export function paymentProblem(sub: ShownSubscription | null | undefined): boolean {
  return sub?.status === "grace" || sub?.status === "on_hold";
}

// --- one billing state (#1010) ----------------------------------------------------------

/**
 * Where a person stands, one state for every screen that offers a plan or credits: the
 * Account card, Home's blocked plate and where `/account?top_up=1` goes. See `billingState`.
 */
export type BillingState =
  | "paused"
  | "payment_failed"
  | "payment_paused"
  | "trial"
  | "trial_low"
  | "no_plan"
  | "no_plan_out"
  | "plan_ok"
  | "plan_low"
  | "plan_out";

/** Under this many credits a plan is running low (an hour of meeting)... */
export const LOW_CREDITS = 60;
/** ...unless its monthly credits reset within this many days anyway. */
const LOW_RESET_DAYS = 3;

/**
 * The billing state, first match wins (#1010):
 * 1. `paused`: the account is blocked;
 * 2. `payment_failed`: the plan's payment failed and it still holds (grace);
 * 3. `payment_paused`: the plan's payment failed and it is paused (on hold);
 * 4. `trial` / `trial_low`: no plan, on the free trial (#1024), under 60 credits for
 *    `trial_low`. An ended or used-up trial is null in the wallet, so it is no plan;
 * 5. `no_plan`: no plan, and a meeting can start;
 * 6. `no_plan_out`: no plan, and the credits are used up;
 * 7. `plan_ok`: a plan, a meeting can start, and it is not running low;
 * 8. `plan_low`: a plan, a meeting can start, under 60 credits, and the reset is more than
 *    3 days away or unknown;
 * 9. `plan_out`: a plan, and the credits are used up.
 * A plan that is ending, or billed in a store, is a flag on top (`planEnding`, `planStore`).
 * "Used up" is the server's own answer (`start_refusal`), never worked out here.
 */
export function billingState(wallet: Wallet, now: number = Date.now()): BillingState {
  const sub = wallet.subscription ?? null;
  const out = wallet.start_refusal === "billing_balance_exhausted";
  if (wallet.blocked) return "paused";
  if (sub?.status === "grace") return "payment_failed";
  if (sub?.status === "on_hold") return "payment_paused";
  if (!wallet.plan && wallet.trial) return wallet.balance_credits < LOW_CREDITS ? "trial_low" : "trial";
  if (!wallet.plan) return out ? "no_plan_out" : "no_plan";
  if (out) return "plan_out";
  return wallet.balance_credits < LOW_CREDITS && !resetsSoon(sub, now) ? "plan_low" : "plan_ok";
}

/**
 * Whether the plan's monthly credits reset within three days: no point offering a pack. A
 * reset date already past is stale, so it counts as unknown.
 */
function resetsSoon(sub: ShownSubscription | null, now: number): boolean {
  if (!sub?.current_period_end) return false;
  const left = new Date(sub.current_period_end).getTime() - now;
  return left >= 0 && left <= LOW_RESET_DAYS * 24 * 60 * 60 * 1000;
}

/** Whether the plan is cancelled and ends at its period's end. */
export function planEnding(wallet: Wallet): boolean {
  return wallet.subscription?.status === "cancelled_active_until_end";
}

/** The store a plan is billed through, when it is not the web's: changed only there. */
export function planStore(wallet: Wallet): StorePlan | null {
  const provider = wallet.subscription?.provider;
  return provider === "apple_iap" || provider === "google_play" ? provider : null;
}

/** A plan billed in the App Store or Google Play. */
export type StorePlan = "apple_iap" | "google_play";

/**
 * What a plan gives a month, the total its monthly credits are counted against: the web's
 * allowance for a web plan, the apps' for a store plan. The subscription's plan, so a plan
 * on hold (the wallet's `plan` null) still has one. Null when the catalogue does not list it.
 */
export function planAllowance(wallet: Wallet): number | null {
  const plan = (wallet.plans ?? []).find((p) => p.plan === wallet.subscription?.plan);
  if (!plan) return null;
  return planStore(wallet) ? plan.app_credits : plan.web_credits;
}

/** The plan card's tone (#1024): the line, its bar and the state line share it. */
export type CardTone = "ink" | "warn" | "danger";

/**
 * The Account plan card (#1024, option A), worked out once so each part says the same:
 * - `trial`: the Free trial's card rather than a plan's;
 * - `left` of `total`: the month's credits against the plan's allowance, or the trial's
 *   against its whole grant (`K.FREE_GRANT_CREDITS`);
 * - `date`: "Resets on" or, for the trial or a plan that is ending, "Ends on"; null after
 *   a paused payment, or with no date;
 * - `extra`: the credits bought as packs, shown only above zero. The trial's own credits
 *   are counted in the wallet's `topup_credits`, so they are taken out here;
 * - `tone`: danger when a meeting can't start or nothing is left, warn running low, else ink.
 */
export interface PlanCardModel {
  trial: boolean;
  left: number;
  total: number;
  date: string | null;
  extra: number;
  tone: CardTone;
}

export function planCard(wallet: Wallet, state: BillingState = billingState(wallet)): PlanCardModel {
  const sub = wallet.subscription ?? null;
  // From the wallet, not the state: a paused account on the trial still has the trial's card.
  const trial = !wallet.plan && sub?.status !== "on_hold" ? wallet.trial ?? null : null;
  const out = wallet.start_refusal === "billing_balance_exhausted" || wallet.balance_credits <= 0;
  const tone: CardTone = out ? "danger" : state === "plan_low" || state === "trial_low" ? "warn" : "ink";
  if (trial) {
    return {
      trial: true,
      left: trial.credits_left,
      total: Math.max(K.FREE_GRANT_CREDITS, trial.credits_left),
      date: t("plans.ends", { date: planDate(trial.ends_at) }),
      extra: Math.max(0, (wallet.topup_credits ?? 0) - trial.credits_left),
      tone,
    };
  }
  const left = Math.max(0, wallet.plan_credits ?? 0);
  const end = sub?.current_period_end ?? null;
  const date =
    !end || state === "payment_paused"
      ? null
      : t(planEnding(wallet) ? "plans.ends" : "plans.resets", { date: planDate(end) });
  return {
    trial: false,
    left,
    total: Math.max(planAllowance(wallet) ?? left, left),
    date,
    extra: Math.max(0, wallet.topup_credits ?? 0),
    tone,
  };
}

/** The card's line: "640 of 900 monthly credits left", or the trial's "trial credits left". */
export function planCardLine(card: PlanCardModel): string {
  return t(card.trial ? "plans.trial_left" : "plans.monthly_left", {
    left: credits(card.left),
    total: credits(card.total),
  });
}

/**
 * Where Add credits really goes (#1010): the packs need a plan, so without one (or when the
 * server says the packs need one) it is the plans. With plans off, always the packs.
 */
export function topUpSheet(wallet: Wallet): "plans" | "packs" {
  if (wallet.packs_require_plan) return "plans";
  return plansOn(wallet) && !wallet.plan ? "plans" : "packs";
}

/** Whether a checkout was refused because packs need a plan (#1009): offer the plans. */
export function planRequired(error: unknown): boolean {
  return (error as { data?: { code?: unknown } } | null)?.data?.code === "billing_plan_required";
}

/** A count with the viewer's thousands separator: "1,200". */
export function credits(count: number, locale?: string): string {
  return Math.trunc(count).toLocaleString(locale);
}

/**
 * What a pack leaves, the last line on its card in Add credits (#1010): "You'll have {n}
 * credits", or, while the balance is below zero and the pack would not leave any, that it
 * leaves none to start a meeting with -- the gate refuses a start at zero -- or how many
 * credits short it would still be.
 */
export function packAfter(balance: number, packCredits: number): string {
  const after = balance + packCredits;
  if (after > 0) return t("billing.pack_after", { credits: credits(after) });
  if (after === 0) return t("billing.pack_none_left");
  return t("billing.pack_still_short", { credits: credits(-after) });
}

type Transaction = Wallet["transactions"][number];

/** What one history row is called: the meeting's own title for a debit. */
export function transactionTitle(tx: Transaction): string {
  const key: Record<string, StringKey> = {
    free_grant: "billing.tx_free_grant",
    topup: "billing.tx_topup",
    session_debit: "billing.tx_meeting",
    overage_writeoff: "billing.tx_writeoff",
    dispute_debit: "billing.tx_dispute",
    dispute_reversal: "billing.tx_dispute_reversal",
    plan_grant: "billing.tx_plan_grant",
    plan_expiry: "billing.tx_plan_expiry",
    trial_expiry: "billing.tx_trial_expiry",
  };
  if (tx.type === "session_debit" && tx.session_title) return tx.session_title;
  return t(key[tx.type] ?? "billing.tx_adjustment");
}

/** The icons a history row can carry, by their names in the shared set. */
export type TransactionIconName =
  | "speaker-notes"
  | "sparkle"
  | "bolt"
  | "timer"
  | "warning"
  | "check-circle"
  | "calendar"
  | "clock";

/**
 * Which icon a history row carries, by its kind (#849): the notes for a meeting, the sparkle
 * for the free credits, the bolt for credits bought, the stopwatch for a meeting's time not
 * charged, the warning for a disputed payment and the tick when the dispute is resolved. A
 * plan's monthly credits, and what was left of them at the renewal, carry the calendar
 * (#875), as does what was left of the free trial when it ended (#1023). A kind the client
 * does not know yet keeps the clock.
 */
export function transactionIcon(tx: Transaction): TransactionIconName {
  const icon: Record<string, TransactionIconName> = {
    session_debit: "speaker-notes",
    free_grant: "sparkle",
    topup: "bolt",
    overage_writeoff: "timer",
    dispute_debit: "warning",
    dispute_reversal: "check-circle",
    plan_grant: "calendar",
    plan_expiry: "calendar",
    trial_expiry: "calendar",
  };
  return icon[tx.type] ?? "clock";
}

/**
 * The signed amount, "+135 credits" or "−45 credits", in three parts so the row can set the
 * signed number apart from the unit: what comes before the number in the contract string
 * (the sign), the number, and what follows it (the unit). The parts come from the string
 * itself, its `{credits}` placeholder left unfilled and split around.
 */
export function transactionAmountParts(tx: Transaction): [before: string, credits: string, after: string] {
  const credits = String(Math.abs(Math.trunc(tx.amount_credits)));
  const [before, after = ""] = t(tx.amount_credits < 0 ? "billing.used" : "billing.added").split("{credits}");
  return [before, credits, after];
}

export interface WalletState {
  wallet: Wallet | null;
  /** The last read failed; `wallet` keeps whatever the read before it returned. */
  failed: boolean;
  reload: () => Promise<Wallet | null>;
}

/**
 * The wallet, read on mount while `enabled` (a signed-in account) and again on `reload`.
 * A signed-out device has no wallet and asks for nothing.
 *
 * On mount it uses the read the launch started when that is still fresh (#1066,
 * `engine/home-data.ts`) instead of asking again. `reload` always asks the server, and
 * drops the launch's read so a later mount does too.
 */
export function useWallet(enabled: boolean): WalletState {
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [failed, setFailed] = useState(false);

  const reload = useCallback(async () => {
    forgetHomeData();
    try {
      const next = await api.wallet();
      setWallet(next);
      setFailed(false);
      return next;
    } catch (err) {
      console.warn("wallet read failed", err);
      setFailed(true);
      return null;
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    (launchWalletRead() ?? api.wallet()).then(
      (next) => {
        if (!alive) return;
        setWallet(next);
        setFailed(false);
      },
      (err) => {
        console.warn("wallet read failed", err);
        if (alive) setFailed(true);
      }
    );
    return () => {
      alive = false;
    };
  }, [enabled]);

  return { wallet, failed, reload };
}

/**
 * The blocked record plate's title and line, by cause. Each says only what the server
 * knows: an empty or negative balance is stated as it is, never as "you ran over" -- the
 * free credits running out, a meeting that ran long and a disputed payment all end there.
 * `wallet` is null when the refusal came from a create and the wallet is not read yet.
 *
 * With plans on (#1010), out of credits says the one remedy there is: Choose a plan
 * without one, Add credits with one, and "Out of credits" under both.
 */
export function blockedPlateCopy(
  refusal: StartRefusal,
  wallet: Wallet | null
): { title: string; subtitle: string } {
  const balance = wallet?.balance_credits ?? null;
  if (refusal === "billing_account_blocked") {
    return { title: t("home.blocked_paused"), subtitle: t("home.blocked_paused_subtitle") };
  }
  if (refusal === "billing_meeting_in_progress") {
    return {
      title: t("home.blocked_other_meeting"),
      subtitle: t("home.blocked_other_meeting_subtitle"),
    };
  }
  if (wallet && plansOn(wallet)) {
    return {
      title: wallet.plan ? t("home.blocked_no_time") : t("home.blocked_choose_plan"),
      subtitle: t("home.blocked_out"),
    };
  }
  return {
    title: t("home.blocked_no_time"),
    subtitle:
      balance !== null && balance < 0
        ? t("home.blocked_owed_subtitle", { credits: Math.abs(Math.trunc(balance)) })
        : t("home.blocked_no_time_subtitle"),
  };
}

/**
 * Where the blocked record plate goes: to the remedy for that cause. Null for a meeting
 * still running (#735): the remedy is End it, and only a create's refusal says which
 * meeting that is and where, so the plate tries the start and Home offers End it.
 * Out of credits goes to the plans without a plan (#1010), else to Add credits; before the
 * wallet is read, `/account?top_up=1` decides once it is.
 */
export function blockedPlateTarget(refusal: StartRefusal, wallet: Wallet | null = null): string | null {
  if (refusal === "billing_balance_exhausted") {
    return wallet && topUpSheet(wallet) === "plans" ? "/plans" : "/account?top_up=1";
  }
  if (refusal === "billing_meeting_in_progress") return null;
  return "/account";
}
